import React, { useState, useEffect } from "react";
import "./MaintenanceDetails.css";
import profileIcon from "../../../shared/assets/icons/profile.png";
import { useNavigate, useParams } from "react-router-dom";
import { taskDetailsService } from "../services/taskDetailsService";
import StepperComponent from "../../../components/StepperComponent";
import apiClient from "../../../shared/services/apiHelper";
import {
  parseAndConvertToLocal,
  twentyFourHrFormat,
} from "../../../shared/utils/dateFormatter";
import { formatCurrency as localFormatCurrency } from "../../../shared/utils/localeHelper";
import { useMaintenanceContext } from "../../../contexts/MaintenanceContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import HeaderNav from "../../../shared/components/HeaderNav";
import { IoArrowUndoOutline } from "react-icons/io5";
import MainLoader from "../../../shared/components/MainLoader";
import { getPriorityColor } from "../../../shared/utils/priorityHelper";
import MobileFooterNav from "../../../shared/components/MobileFooterNav";

const MaintenanceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { maintenanceData, isLoading, fetchMaintenanceDetails } = useMaintenanceContext();
  const { showSnackbar } = useSnackbar();

  const serviceCategoryList = [
    { label: "PAID", value: 0 },
    { label: "UNDER WARRANTY", value: 1 },
    { label: "AMC", value: 3 },
  ];

  const getServiceCategoryLabel = (value) => {
    const category = serviceCategoryList.find((item) => item.value === value);
    return category ? category.label : "N/A";
  };

  const [statusCodes, setStatusCodes] = useState([]);
  const [isEstimationConfirming, setIsEstimationConfirming] = useState(false);
  const [isWorkReportConfirming, setIsWorkReportConfirming] = useState(false);

  const showWorkOrder = statusCodes.includes(32);
  const showEstimation = statusCodes.includes(24);

  const getPriorityText = (priority) => {
    switch (priority) {
      case 0: return "LOW";
      case 1: return "MEDIUM";
      case 2: return "NORMAL";
      case 3: return "HIGH";
      case 4: return "URGENT";
      default: return "NORMAL";
    }
  };

  const formatCurrency = (amount) =>
    amount !== null && amount !== undefined ? localFormatCurrency(amount) : "N/A";

  useEffect(() => {
    const fetchStatusCodes = async () => {
      if (!id) return;
      try {
        const response = await taskDetailsService.getMaintenanceRequestDetails(id, "task_history");
        if (response.success && response.data) {
          const codes = response.data.map((item) => item.status).filter((s) => s != null);
          setStatusCodes(codes);
        }
      } catch (error) {
        console.error("Error fetching status codes:", error);
      }
    };
    fetchStatusCodes();
  }, [id]);

  useEffect(() => {
    if (id) fetchMaintenanceDetails(id);
  }, [id]);

  useEffect(() => {
    const handleRefresh = (event) => {
      if (event.detail?.id && event.detail.id === id) fetchMaintenanceDetails(id);
    };
    window.addEventListener("refreshMaintenanceDetails", handleRefresh);
    return () => window.removeEventListener("refreshMaintenanceDetails", handleRefresh);
  }, [id, fetchMaintenanceDetails]);

const handleInvoiceGeneration = async () => {
  const workReportCompleted = statusCodes.includes(32);

  if (!workReportCompleted) {
    showSnackbar(
      "Invoice unavailable: work report is not completed yet.",
      "error"
    );
    return;
  }

  try {
    const pdfUrl = await taskDetailsService.generateInvoice(
      maintenanceData?.id?.toString() || ""
    );

    if (pdfUrl && pdfUrl !== "000") {
      navigate("/pdf-view", { state: { pdfUrl, title: "Invoice" } });
    } else {
      showSnackbar("Invoice is not available yet.", "error");
    }
  } catch (error) {
    console.error(error);
    showSnackbar("Error generating invoice.", "error");
  }
};

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#F6F8FC", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <MainLoader />
      </div>
    );
  }

  return (
    <div className="maintenance-request">
      <HeaderNav profileIcon={profileIcon} />

      <div className="second-header">
        <button className="back-button">
          <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate("/tasklist")} />
        </button>
        <h2 className="page-title">Maintenance Details</h2>
      </div>

      <header className="mobile-header">
        <div className="header-left">
          <IoArrowUndoOutline color="black" size={25} alt="Back" onClick={() => navigate("/tasklist")} />
        </div>
        <h1 className="app-title">Maintenance Details</h1>
      </header>

      <div className="workorder-container">
        <div className="product-card">
          <div className="product-title-row">
            <div className="product-name-row">
              {maintenanceData?.equipment?.equipmentName && (
                <span className="product-name">{maintenanceData.equipment.equipmentName}</span>
              )}
              {maintenanceData?.equipment?.equipmentName && maintenanceData?.equipment?.product && (
                <span className="product-make">&nbsp;| {maintenanceData.equipment.product}</span>
              )}
              {!maintenanceData?.equipment?.equipmentName && maintenanceData?.equipment?.product && (
                <span className="product-make">{maintenanceData.equipment.product}</span>
              )}
            </div>
          </div>

          <div className="status-warranty-row">
            <span className="status ongoing" style={(() => {
              const p = maintenanceData?.schedule?.priorityRaw;
              const color = getPriorityColor(p);
              return { background: color + "22", color, borderColor: color + "66" };
            })()}>
              {getPriorityText(maintenanceData?.schedule?.priorityRaw) || ""}
            </span>
            <span className="warranty">
              {maintenanceData?.equipment?.model}
              {maintenanceData?.equipment?.model && getServiceCategoryLabel(maintenanceData?.warrantyType)
                ? ` | ${getServiceCategoryLabel(maintenanceData?.warrantyType)}`
                : getServiceCategoryLabel(maintenanceData?.warrantyType) || ""}
            </span>
            <span className="warranty">
              <strong>Request ID:</strong> {maintenanceData?.reqId || "N/A"}
            </span>
          </div>

          <div className="date-price-row">
            <div className="reported-date">
              <span className="label">Reported On</span>
              <span className="value">
                {maintenanceData?.reportedDate
                  ? twentyFourHrFormat(parseAndConvertToLocal(maintenanceData.reportedDate), true)
                  : "N/A"}
              </span>
            </div>
            <div className="total-price">
              {showWorkOrder && maintenanceData?.workReport?.totalAmount ? (
                <>
                  <span className="label">Invoice Amount</span>
                  <span className="value">{formatCurrency(maintenanceData.workReport.totalAmount)}</span>
                </>
              ) : showEstimation && maintenanceData?.estimation?.totalAmount ? (
                <>
                  <span className="label">Estimated Amount</span>
                  <span className="value">{formatCurrency(maintenanceData.estimation.totalAmount)}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="service-status-header">
          <h3>Your Service Status</h3>
          <div className="action-links">
            <span onClick={() => navigate(`/tasklist?equipment_id=${maintenanceData?.equipment?.id}`)} style={{ cursor: "pointer" }}>View History</span>
            <span> | </span>
            <span onClick={() => navigate(`/checklist/${id}`)} style={{ cursor: "pointer" }}>Checklist</span>
            <span> | </span>
            <span onClick={handleInvoiceGeneration} style={{ cursor: "pointer" }}>Invoice</span>
          </div>
        </div>

        <StepperComponent
          taskId={id}
          isMobile={false}
          reportedDate={maintenanceData?.reportedDate}
          maintenanceData={maintenanceData}
          statusCodes={statusCodes}
          onEstimationConfirm={async (blob, name) => {
            setIsEstimationConfirming(true);
            try {
              const response = await apiClient.uploadSingleFile({
                endpoint: "/task/estimation-confirm",
                imageFile: blob,
                additionalData: { task_id: parseInt(id), name },
                keyName: "file",
              });
              if (response.data?.success) { alert("Estimation confirmed successfully!"); window.location.reload(); }
              else alert(response.data?.message || "Failed to confirm estimation");
            } catch (e) {
              alert(e?.response?.data?.message || "Error confirming estimation");
            } finally {
              setIsEstimationConfirming(false);
            }
          }}
          onWorkReportConfirm={async (blob, name) => {
            setIsWorkReportConfirming(true);
            try {
              const response = await apiClient.uploadSingleFile({
                endpoint: "/task/confirm-report",
                imageFile: blob,
                additionalData: { task_id: parseInt(id), name, remarks: "" },
                keyName: "file",
              });
              if (response.data?.success) { alert("Work report confirmed successfully!"); window.location.reload(); }
              else alert("Failed to confirm work report");
            } catch (e) {
              console.error("Work report confirm error:", e);
              alert("Error confirming work report");
            } finally {
              setIsWorkReportConfirming(false);
            }
          }}
          onFeedbackSubmit={async ({ rating, comment, tags }) => {
            const data = await taskDetailsService.submitFeedback({ task_id: parseInt(id), rating, comment, tags });
            if (data.success) return true;
            alert("Error submitting feedback");
            return false;
          }}
        />
      </div>
      <MobileFooterNav />
    </div>
  );
};

export default MaintenanceDetails;
